/**
 * SB5-B.2 — projects/v1 contract-positions wire types.
 *
 * Hand-authored to mirror the canonical contract at
 * `_BACKEND/contracts/projects/v1/openapi.yaml` (schemas
 * `ContractPositionListItem`, `ContractPositionListResponse`,
 * `ContractPositionDetail`, `ContractPositionState`,
 * `ContractPositionUpdateRequest`, `ContractPositionCreateRequest`,
 * `ContractPositionUserRefV1`, `ContractPositionsSortKey`).
 *
 * SYNC: projects/v1@2.6.0 — TNCY-8 added the cross-tenant ownership tell
 * (`ownership`/`ownerTenantName`/`role`) on `ContractPositionListItem` and a
 * `scope=own|member` query param on the list. The FE fires both scopes and
 * merges them into one table (`useOwnerMemberContractPositions`, TNCY-9).
 *
 * Per Q14: ContractPositionState lives ONLY here in FE-land — never
 * re-declared. The 6 canonical values mirror the BE enum verbatim.
 */

export const CONTRACT_POSITION_STATES = [
  "CREATED",
  "SCHEDULED",
  "EXECUTION",
  "COMPLETED",
  "WITHDRAWN",
  "REJECTED",
] as const;
export type ContractPositionState = (typeof CONTRACT_POSITION_STATES)[number];

export const CONTRACT_POSITIONS_SORT_KEYS = [
  "positionCode",
  "projectName",
  "siteName",
  "assetDisplayLabel",
  "state",
] as const;
export type ContractPositionsSortKey =
  (typeof CONTRACT_POSITIONS_SORT_KEYS)[number];

export type ContractPositionsSortDir = "asc" | "desc";

/** SYNC: projects/v1@2.6.0 (TNCY-8) — which side of the cross-tenant fence a
 *  position row is on. `owner` = a position of the caller's own contract
 *  (default `scope=own`); `member` = a position of a contract the caller was
 *  granted cross-tenant read access to (`scope=member`, via
 *  `project_contractor` or a `contract_contractor` grant, XT-D22). */
export type ContractPositionOwnership = "owner" | "member";

/** SYNC: projects/v1@2.6.0 (TNCY-8) — read/list scope. `own` (default) =
 *  caller's own positions; `member` = the cross-tenant fan-out of positions
 *  of granted contracts. The FE fires both and merges them into one list
 *  (`useOwnerMemberContractPositions`). */
export type ContractPositionsScope = "own" | "member";

/** SYNC: projects/v1@2.6.0 / XT-D22 — the caller's access role on a `member`
 *  position. Mirrors the `tenancy/v1` AccessRole enum; null on `owner` rows. */
export const CONTRACT_POSITION_ACCESS_ROLES = [
  "project_customer",
  "project_contractor",
  "contract_customer",
  "contract_contractor",
  "reporting_subcontractor",
  "ii_subcontractor",
] as const;
export type ContractPositionAccessRole =
  (typeof CONTRACT_POSITION_ACCESS_ROLES)[number];

export interface ContractPositionListItem {
  contractPositionId: string;
  positionCode: number;
  projectId: string;
  projectName: string;
  siteId: string | null;
  siteName: string | null;
  assetId: string;
  assetDisplayLabel: string;
  state: ContractPositionState;
  // SB6-G2 Bug-6 — widened to `unknown` because the gateway emits
  // contract positions with `external_ids: []` (raw array default) AND
  // `external_ids: {sap: "K-1"}` (canonical map). The ExternalIdsEditor
  // parser (`externalIdsObjectToRows`) accepts both shapes.
  externalIds: unknown;
  createdAt: string;
  updatedAt: string;
  /** PEDIT-TREE (projects/v1 additive on the held BE branch) — the parent
   *  contract's id, so the project entity tree can link a position under its
   *  contract client-side. Optional for back-compat with a BE that predates the
   *  additive field; absent ⇒ the position groups under an "Unlinked" bucket.
   *  Hand-added to this hand-rolled wire type (the projects domain has no
   *  openapi-typescript codegen — see CLAUDE.md "Contracts & codegen"). */
  contractId?: string | null;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): `owner` | `member`. Optional for
   *  back-compat with a pre-2.6.0 BE; always present from 2.6.0. Absent ⇒
   *  treated as `owner`. */
  ownership?: ContractPositionOwnership;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): the data-owning tenant's display name
   *  on a `member` row; null on `owner` rows. */
  ownerTenantName?: string | null;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): the caller's access role on a `member`
   *  position's project (XT-D22); null on `owner` rows. */
  role?: ContractPositionAccessRole | null;
}

export interface ContractPositionListResponse {
  items: ContractPositionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ContractPositionUserRef {
  firstName: string | null;
  lastName: string | null;
  tenantName: string | null;
}

export interface ContractPositionDetail {
  contractPositionId: string;
  positionCode: number;
  contractId: string;
  contractCode: number;
  projectId: string;
  projectName: string;
  siteId: string | null;
  siteName: string | null;
  assetId: string;
  assetDisplayLabel: string;
  state: ContractPositionState;
  pricing: Record<string, unknown>;
  // SB6-G2 Bug-6 — see ContractPositionListItem.externalIds comment.
  externalIds: unknown;
  createdBy: ContractPositionUserRef;
  createdAt: string;
  updatedBy: ContractPositionUserRef;
  updatedAt: string;
  deletedBy: ContractPositionUserRef | null;
  deletedAt: string | null;
}

export interface ContractPositionUpdateRequest {
  pricing?: Record<string, unknown>;
  externalIds?: Record<string, unknown>;
}

export interface ContractPositionCreateRequest {
  contractId: string;
  assetId: string;
  pricing?: Record<string, unknown>;
  externalIds?: Record<string, unknown>;
}

export interface ContractPositionsFacetOption {
  id: string;
  label: string;
  count?: number;
}

export interface ContractPositionsFacetResponse {
  items: ContractPositionsFacetOption[];
}

export interface ListContractPositionsParams {
  q?: string;
  projectIds?: readonly string[];
  siteIds?: readonly string[];
  assetIds?: readonly string[];
  states?: readonly ContractPositionState[];
  includeCompleted?: boolean;
  sortBy?: ContractPositionsSortKey;
  sortDir?: ContractPositionsSortDir;
  page?: number;
  pageSize?: number;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): `own` (default, single-schema fast
   *  path) | `member` (cross-tenant fan-out of positions of granted
   *  contracts). Omitted ⇒ BE defaults to `own`. The FE fires both and merges
   *  (`useOwnerMemberContractPositions`). */
  scope?: ContractPositionsScope;
}
